// A schema describes a list's columns once; it drives the template, parsing,
// validation and the diff, so a consumer supplies only a schema and a commit callback.

/** Separator for list-valued cells. Commas are unusable inside CSV without quoting. */
export const CSV_LIST_SEPARATOR = ';';

/**
 * Splits a list cell on semicolons or commas, since spreadsheet users reach for commas.
 * A value containing a comma ("Smith, John") silently becomes two, so don't use this for prose.
 */
export const splitList = (raw: string): string[] =>
  raw
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter((part) => part !== '');

/** Inverse of `splitList`. */
export const joinList = (values: readonly string[] | null | undefined): string =>
  (values ?? []).join(CSV_LIST_SEPARATOR);

/** The schema's spelling of `value`, matched case-insensitively, or undefined if not allowed. */
export const canonicalEnumValue = (field: CsvField, value: string): string | undefined =>
  field.enumValues?.find((allowed) => allowed.toLowerCase() === value.trim().toLowerCase());

/** Whether a header cell names a field, ignoring case and surrounding spaces. */
export const sameLabel = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Marks the template's example rows; readers drop any row whose first cell starts with it,
 * so leftover examples are never imported. Visible so a user copying an example sees it.
 */
export const CSV_COMMENT_PREFIX = '#';

export type CsvFieldType = 'string' | 'number' | 'boolean' | 'date' | 'enum' | 'phone' | 'email';

/** A column's width in the review grid, from narrow to wide. */
export type CsvColumnWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type CsvField = {
  /** Property this column maps to on a record. */
  key: string;
  /** Column header, and how the field is named in validation errors. */
  label: string;
  /** Review grid header when `label` is too long; the CSV, template and messages keep `label`. */
  shortLabel?: string;
  type: CsvFieldType;
  /** The cell holds a list of values of `type`. */
  multiple?: boolean;
  /** Compare exactly as written (case and list order included), e.g. for a tie-breaker label. */
  compareLiterally?: boolean;
  /** Set when a record is added; a different value on an existing record is an error. */
  fixedOnceAdded?: boolean;
  /** Why this field can't change on one existing record, or undefined when it can. */
  lockedFor?: (current: Record<string, string>) => string | undefined;
  /** For a `multiple` field: a cell's values are added to a record's, never removed. */
  addOnly?: boolean;
  /** A change worth the reviewer's attention, such as access: counted on the confirm step. */
  sensitive?: boolean;
  /** For a `phone` field: where an extension written into the number ("... x204") is moved on read; a value already there wins. */
  extensionField?: string;
  required?: boolean;
  /**
   * Why this field can't be empty on one row, given the row's values by key, or undefined
   * when it can. For a column only some rows need.
   */
  requiredFor?: (row: Record<string, string>) => string | undefined;
  /** Allowed values, for `enum` fields. Drawn on for the template's examples. */
  enumValues?: string[];
  /** Value for the template's example rows; defaults to a plausible value for the type. */
  example?: string;
  /** Overrides the review grid's width for the field's type. */
  width?: CsvColumnWidth;
  /** Characters accepted, both when typing in the review grid and when reading a file. */
  allow?: RegExp;
  /** Extra per-cell validation; returns an error message, or undefined when valid. */
  validate?: (value: string) => string | undefined;
};

export type CsvSchema = {
  /** Ordered: this is the column order in the template and in the parsed result. */
  fields: CsvField[];
  /**
   * Field(s) matching an uploaded row to an existing record. Use several when none is
   * unique alone, e.g. a branch sharing one number told apart by extension.
   */
  keyColumn: string | string[];
  /** Key field suggested in clash errors as the one to change to keep both rows. */
  tieBreaker?: string;
};

/** The key fields of `schema`, however it spelled them. */
export const keyColumns = (schema: CsvSchema): string[] =>
  Array.isArray(schema.keyColumn) ? schema.keyColumn : [schema.keyColumn];
